// Purpose: let an operator turn call recording on or off for this subaccount.
//
// Recording changes what is captured about callers, so the panel is explicit
// rather than a bare switch:
//
//   - the off state is shown as the real state, not an absence of configuration
//   - turning it on requires choosing how consent is handled and the notice text
//   - the API re-validates the same rules, so the UI cannot store an unclear state
//
// Defaults offered when enabling are the least surprising ones, but nothing is
// saved until the operator submits.

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import {
  ConsoleApiError,
  fetchRecordingPolicy,
  isUnauthorizedApiError,
  updateRecordingPolicy,
} from './apiClient';
import type { ApiRecordingPolicy } from './apiClient';

const DEFAULT_NOTICE = 'This call is recorded for quality and training purposes.';

export function RecordingPolicyPanel(props: { onSessionExpired: () => void }) {
  const { onSessionExpired } = props;
  const [policy, setPolicy] = useState<ApiRecordingPolicy | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    fetchRecordingPolicy()
      .then((loaded) => {
        if (cancelled) return;
        setPolicy(loaded);
        setLoadState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isUnauthorizedApiError(error)) {
          onSessionExpired();
          return;
        }
        setLoadState('error');
        setMessage(error instanceof ConsoleApiError ? error.message : 'Could not load recording policy');
      });
    return () => {
      cancelled = true;
    };
  }, [onSessionExpired]);

  function toggleEnabled(enabled: boolean) {
    setPolicy((current) => {
      if (!current) return current;
      if (!enabled) {
        // Turning recording off clears what was permitted, so the stored state
        // matches what an operator believes they just switched off.
        return { ...current, enabled: false, allowedArtifactTypes: [], consentMode: 'disabled' };
      }
      return {
        ...current,
        enabled: true,
        allowedArtifactTypes: current.allowedArtifactTypes.length
          ? current.allowedArtifactTypes
          : ['full_call'],
        consentMode: current.consentMode === 'disabled' ? 'notice_required' : current.consentMode,
        consentNotice: current.consentNotice ?? DEFAULT_NOTICE,
      };
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!policy) return;
    setMessage('Saving recording policy…');
    try {
      const saved = await updateRecordingPolicy({
        enabled: policy.enabled,
        retentionDays: policy.retentionDays,
        allowedArtifactTypes: policy.allowedArtifactTypes,
        consentMode: policy.consentMode,
        consentNotice: policy.consentNotice,
      });
      setPolicy(saved);
      setMessage(saved.enabled ? 'Recording is enabled for this workspace.' : 'Recording is off for this workspace.');
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setMessage(error instanceof ConsoleApiError ? error.message : 'Could not save recording policy');
    }
  }

  if (loadState === 'loading') {
    return <section className="panel"><h2>Call recording</h2><div className="empty-state">Loading recording policy…</div></section>;
  }
  if (loadState === 'error' || !policy) {
    return <section className="panel"><h2>Call recording</h2><div className="api-banner warning">{message ?? 'Could not load recording policy.'}</div></section>;
  }

  return (
    <section className="panel" aria-labelledby="recording-policy-heading">
      <h2 id="recording-policy-heading">Call recording</h2>
      <p className="compact-copy">
        Controls whether calls in this workspace are recorded. Recording is off unless you turn it on,
        and enabling it requires choosing how callers are told.
      </p>
      {policy.inherited ? (
        <p className="compact-copy">No policy is set for this workspace, so recording is off.</p>
      ) : null}
      {message ? <p className="compact-copy" role="status">{message}</p> : null}

      <form className="form-stack" onSubmit={(event) => void handleSubmit(event)}>
        <label className="auth-field">
          <span>Record calls</span>
          <select
            className="compact-select"
            value={policy.enabled ? 'on' : 'off'}
            onChange={(event) => toggleEnabled(event.target.value === 'on')}
          >
            <option value="off">Off</option>
            <option value="on">On</option>
          </select>
        </label>

        {policy.enabled ? (
          <>
            <label className="auth-field">
              <span>Consent handling</span>
              <select
                className="compact-select"
                value={policy.consentMode}
                onChange={(event) =>
                  setPolicy({ ...policy, consentMode: event.target.value as ApiRecordingPolicy['consentMode'] })
                }
              >
                <option value="notice_required">Notify the caller</option>
                <option value="explicit_opt_in">Require the caller to opt in</option>
              </select>
            </label>

            <label className="auth-field">
              <span>What to record</span>
              <select
                className="compact-select"
                value={policy.allowedArtifactTypes[0] ?? 'full_call'}
                onChange={(event) => setPolicy({ ...policy, allowedArtifactTypes: [event.target.value] })}
              >
                <option value="full_call">Whole call</option>
                <option value="agent_only">Agent audio only</option>
                <option value="user_only">Caller audio only</option>
                <option value="mixed_streams">Separate streams</option>
              </select>
            </label>

            <div className="field-stack">
              <label className="auth-field">
                <span>Consent notice</span>
                <input
                  value={policy.consentNotice ?? ''}
                  onChange={(event) => setPolicy({ ...policy, consentNotice: event.target.value })}
                  required
                />
              </label>
              <small className="compact-copy">
                Wording callers are given. Check this meets the rules for the places you operate in.
              </small>
            </div>

            <div className="field-stack">
              <label className="auth-field">
                <span>Keep recordings for (days)</span>
                <input
                  type="number"
                  min="1"
                  max="3650"
                  value={policy.retentionDays}
                  onChange={(event) => setPolicy({ ...policy, retentionDays: Number(event.target.value) })}
                  required
                />
              </label>
              <small className="compact-copy">Recordings are deleted after this period.</small>
            </div>
          </>
        ) : null}

        <button className="primary-button" type="submit">Save recording policy</button>
      </form>
    </section>
  );
}
